import React, { useCallback, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import type { LinkPickerPlugin, LinkPickerProps } from '@atlaskit/link-picker';
import { LazyLinkPicker } from '@atlaskit/link-picker/lazy';
import type { ContentProps } from '@atlaskit/popup/types';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, xcss } from '@atlaskit/primitives';
import { useProductDependencies } from '@atlassian/company-hub-common';
import { useLinkPickerPlugins } from '@atlassian/link-picker-plugins';

import { LINKS_MENU_EXTENSION_ANALYTIC_SOURCE } from '../../../../../../constants';
import {
	createNewLinkItemWithDefaultParameters,
	createNewMenuItemWithDefaultParameters,
	type LinksMenuExtensionActions,
} from '../../../../../../controllers/use-links-menu-parameters-and-actions';
import { ITEM, type ItemType, type LinkItemType } from '../../../../../../types';
import { DisplayTextField } from './display-text-field';
import { messages } from './messages';

const configStyles = xcss({
	margin: 'space.200',
	width: '310px',
});

const buttonStyles = xcss({
	marginTop: 'space.200',
	justifyContent: 'flex-end',
	gap: 'space.050',
});

const linkPickerCustomMessages = defineMessages({
	submitButtonLabel: {
		id: 'company-hub.links-menu-extension.config-panel.property-config.link.submit-button-label',
		defaultMessage: 'Confirm',
		description:
			'Text for the confirm button shown on the footer of the add menu form when the user selects the "Link" option in the "New Item" popup in the links menu configuation panel.',
	},
});

interface BaseConfigProps {
	extensionActions: LinksMenuExtensionActions;
	handleClose: () => void;
	update: ContentProps['update'];
}
// should always pass an existing item, but not a new item type or parent id
interface EditConfigProps extends BaseConfigProps {
	existingItem: ItemType;
	newItemType?: never;
	parentId?: never;
}
// should only pass a new item type an optionally a parent id, but never an existing item
interface NewConfigProps extends BaseConfigProps {
	existingItem?: never;
	newItemType: ITEM;
	parentId?: string;
}
type LinksMenuItemConfigProps = EditConfigProps | NewConfigProps;

const supportedPluginKeys = ['confluence', 'jira', 'atlas', 'loom'];

export const LinksMenuItemConfig: React.FC<LinksMenuItemConfigProps> = ({
	extensionActions,
	handleClose,
	existingItem,
	newItemType,
	parentId,
	update,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addItem, updateItem } = extensionActions;
	const { cloudId, linkPickerConfig } = useProductDependencies();

	// If create new type is passed in, create a new item with default parameters
	const [item] = useState<ItemType>(() => {
		if (newItemType === ITEM.LINK) {
			return createNewLinkItemWithDefaultParameters();
		} else if (newItemType === ITEM.MENU) {
			return createNewMenuItemWithDefaultParameters();
		} else {
			return existingItem!;
		}
	});

	const [textFieldValue, setTextFieldValue] = useState<string>(item.title);
	const [isError, setIsError] = useState<boolean>(false);

	const handleTextFieldChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTextFieldValue(value);
		setIsError(false);
	}, []);

	// used for link item, extra step to handle link field
	const handleSubmitForLink = (payload: Parameters<LinkPickerProps['onSubmit']>[0]) => {
		// The link picker automatically submits when users select a
		// suggested link from the results, even if they have not
		// specified a title yet or hit Confirm. Here we interrupt
		// auto submission and set the display text to the link's title,
		// if none exists and we have a title from linking platform.
		// Then, when users hit Confirm manually the submission will happen.
		if (textFieldValue.length === 0 && payload.title) {
			// If we have a page title from the link picker then set
			// empty display text to that value
			setTextFieldValue(payload.title);
		} else {
			// submit the update with link field
			handleSubmit(payload.url);
		}
	};

	const handleSubmit = (link?: string) => {
		// do validation for display text field
		if (textFieldValue.length === 0) {
			setIsError(true);
			return;
		}

		// add item if new
		if (!!newItemType) {
			// set properties on new item
			item.title = textFieldValue;
			if (link) {
				(item as LinkItemType).link = link;
			}
			addItem(item, parentId);
		} else {
			updateItem(item, textFieldValue, link);
		}

		createAnalyticsEvent?.({
			type: 'sendTrackEvent',
			data: {
				action: !!newItemType ? 'added' : 'updated',
				actionSubject: 'item',
				source: LINKS_MENU_EXTENSION_ANALYTIC_SOURCE,
				attributes: {
					itemType: (item as LinkItemType).link ? 'link' : 'menu',
				},
			},
		}).fire();

		handleClose();
	};

	const { isLoading, plugins } = useLinkPickerPlugins({
		cloudId,
		product: 'confluence',
	});
	const filteredPlugins = useMemo(() => {
		return plugins.filter((plugin: LinkPickerPlugin) => {
			return plugin.tabKey && supportedPluginKeys.includes(plugin.tabKey);
		});
	}, [plugins]);

	// Use custom plugins if provided, otherwise use default filtered plugins
	const pluginsToUse = linkPickerConfig?.linkPickerPlugins || filteredPlugins;

	const isLoadingPlugins = linkPickerConfig?.linkPickerPlugins ? false : isLoading;

	return (
		<Flex xcss={configStyles} direction="column">
			<DisplayTextField
				itemId={item.itemId}
				displayText={textFieldValue}
				handleOnChange={handleTextFieldChange}
				isInvalid={isError}
			/>
			{item.type === ITEM.LINK ? (
				<LazyLinkPicker
					plugins={pluginsToUse}
					isLoadingPlugins={isLoadingPlugins}
					url={item.link}
					displayText={item.title}
					onSubmit={handleSubmitForLink}
					onCancel={handleClose}
					hideDisplayText
					paddingLeft="0px"
					paddingRight="0px"
					paddingBottom="0px"
					disableWidth
					customMessages={linkPickerCustomMessages}
					onContentResize={update}
				/>
			) : (
				<Flex xcss={buttonStyles}>
					<Button appearance="subtle" onClick={handleClose}>
						{formatMessage(messages.cancelButtonLabel)}
					</Button>
					<Button appearance="primary" onClick={() => handleSubmit()}>
						{formatMessage(messages.confirmButtonLabel)}
					</Button>
				</Flex>
			)}
		</Flex>
	);
};
